import { Device as TwilioDevice } from '@twilio/voice-sdk';
import { v4 as uuidv4 } from 'uuid';
import TwilioVoiceService from './TwilioVoiceService'; // Import necessary dependencies and modules.
import debounce from 'debounce';
import SaleforceService from './SaleforceService';
import { store } from '../app/store';
import { CallActivityDirectionEnum } from '../Enums/CallActivityActionType';
import { addCallTranscript, setInterimTranscription, addBookmark } from '../slices/callTranscript';
import RealTimeClient from './RealTimeClient';
import { logger } from '../Util/Logger/ClientLogger';

// Define types for your custom enums and other types used in the class.
enum RttCommandsEnum {
    // Define your enum values here.
    DISCONNECT = "normal_disconnect",
    STREAM_DATA_MIC = "stream_mic",
    STREAM_DATA_PHONE = "stream_phone",
    METADATA = "metadata",
    START_PROCESSING = "start_processing",
    MANUAL_BOOKMARK = "manual_bookmark"
}

enum RttResponseEnum {
    OK = "ok",
    READY = "ready",
    CONTINUE = "continue",
    ERROR = "error",
    TRANSCRIPT = "transcript",
    BOOKMARK = "bookmark",
    MANUAL_BOOKMARK = "manual_bookmark",
    UNKNOWN = "unknown",
}

// enum RttManualBookmarkEventType {
//     Commit = 'Commit',
//     CommitSuccess = 'CommitSuccess',
//     CommitFailure = 'CommitFailure',
// }

const LogTitle = 'StreamProcessService';

type DebouncedSend = (() => void) & { clear: () => void; flush: () => void };

/** WebSocket `readyState` to short string for logs */
const wsStateLabel = (r?: number) =>
    r === 0 ? 'connecting' : r === 1 ? 'open' : r === 2 ? 'closing' : r === 3 ? 'closed' : 'n/a';

/** Client is replacing this socket; do not count as network failure. Use 3xxx–4xxx in browsers. */
const WS_CLOSE_RECONNECT = 4000;
const WS_CLOSE_RECONNECT_REASON = 'rp-reconnect';
/** When WS is down, cap queue size so encoders do not OOM; oldest chunks are dropped. */
const BUFFER_MAX_WHILE_WS_DOWN = 320;
/** Reconnect + resend; stop when reached (each failed cycle increments). 0 = unlimited. */
const RECONNECT_MAX_ATTEMPTS = 0;
/** Capped exponential backoff: base 800ms, max 30s */
const RECONNECT_BASE_MS = 800;
const RECONNECT_MAX_DELAY_MS = 30_000;
/** After encoders stop, keep the old epoch long enough for the last `ondataavailable` to queue (ms). */
const STOP_RTT_ENCODER_TAIL_MS = 150;
/** Bound hang-up flush (encoder tail + CONTINUE send + socket bufferedAmount) so we cannot leak forever. */
const STOP_RTT_FLUSH_MAX_MS = 8_000;
/** Poll `WebSocket.bufferedAmount` after the last send before close. */
const STOP_RTT_SOCKET_DRAIN_POLL_MS = 50;
const STOP_RTT_SOCKET_DRAIN_MAX_MS = 2_000;
/** Best-effort reconnects if the socket is down at hang-up and the queue is not empty. */
const FLUSH_RECONNECT_MAX_ATTEMPTS = 3;
/** Coalesce ICE / track / reconnected rebuilds so we do not thrash the audio graph. */
const PHONE_REBUILD_DEBOUNCE_MS = 300;
/**
 * Twilio raises `reconnected` as soon as signalling recovers — the replacement remote track can arrive
 * seconds later, so keep re-checking instead of giving up after one pass.
 */
const PHONE_REBUILD_RETRY_MS = 700;
const PHONE_REBUILD_MAX_ATTEMPTS = 25;
/**
 * Local mic is often stopped once Twilio moves it onto the RTCRtpSender. The replacement is not on
 * `getLocalStream()` yet, and Chrome sometimes never fires `ended`, so keep looking for a live track.
 */
const MIC_REBUILD_DEBOUNCE_MS = 200;
const MIC_REBUILD_RETRY_MS = 500;
const MIC_REBUILD_MAX_ATTEMPTS = 20;
const MIC_HEALTH_CHECK_MS = 2_000;
/** While the call is open, don't hammer getUserMedia if the mic stays ended. */
const MIC_REACQUIRE_COOLDOWN_MS = 5_000;
/**
 * Unacked pages on the socket. One page per CONTINUE stalls when the round trip is long;
 * 4 is about 1.6s at 2.5 pages/s. Mic and phone share this queue.
 */
const MAX_PAGES_IN_FLIGHT = 4;
/** Client-side per-track produce counters — makes stale-track silence obvious without server forensics. */
const BYTES_LOG_INTERVAL_MS = 10_000;
/** RP1 framed binary: magic + version + track + flags + uint16-LE seq + Ogg page. */
const AUDIO_FRAME_VERSION = 1;
const AUDIO_TRACK_MIC = 0;
const AUDIO_TRACK_PHONE = 1;

class StreamProcessService {
    private _callRecordId: string;
    private _streamConnection: any;
    private _audioContext: AudioContext | null | undefined;
    private _micStreamSource = null;
    private _encoderOptions = {};
    private _phoneStreamSource = null;
    private _micMediaRecorder = null;
    private _phoneMediaRecorder = null;
    private _websocket: WebSocket | null = null;
    private _buffer: any[] = []; // Define a more specific type for buffer if possible.
    private _camturedBookmark: any[] = [];

    private _connected: boolean = false;
    private _metadataSent: boolean = false;
    private _processing: boolean = false;
    private _stopRequested: boolean = false;

    private _rtaspUrl: string = '';
    private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private _reconnectFailureCount = 0; // +1 on each bad WS close while in call; reset to 0 on (re)open
    private _peerStateListenerAttached: boolean = false;
    private _pcForPeerLog: RTCPeerConnection | null = null;
    private _peerStateLogHandler: (() => void) | null = null;
    private _peerTrackHandler: ((event: RTCTrackEvent) => void) | null = null;
    /** True from first line of `stop` until final teardown; blocks produce / ICE rebuild, not flush send. */
    private _rttStopInProgress = false;
    /** Call ended: stop encoding, keep sending queued pages over CONTINUE until empty (or timeout). */
    private _flushingAfterStop = false;
    private _encoderTailDone = false;
    private _flushReconnectCount = 0;
    private _rttPostStopDrainTimer: ReturnType<typeof setTimeout> | null = null;
    private _flushDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
    private _socketDrainTimer: ReturnType<typeof setTimeout> | null = null;
    private _socketDrainWaitedMs = 0;

    private _phoneTrack: MediaStreamTrack | null = null;
    private _micTrack: MediaStreamTrack | null = null;
    private _phoneTrackEndedHandler: ((this: MediaStreamTrack, ev: Event) => void) | null = null;
    private _phoneTrackMuteHandler: ((this: MediaStreamTrack, ev: Event) => void) | null = null;
    private _micTrackEndedHandler: ((this: MediaStreamTrack, ev: Event) => void) | null = null;
    private _phoneRebuildTimer: ReturnType<typeof setTimeout> | null = null;
    private _micRebuildTimer: ReturnType<typeof setTimeout> | null = null;
    private _micHealthTimer: ReturnType<typeof setInterval> | null = null;
    /** Hold / explicit mute. A rebuilt mic encoder must stay silent until unmute. */
    private _captureMuted = false;
    private _micTrackSource: string | null = null;
    /** Stop the health check from restarting a full retry loop after we already gave up. */
    private _micReattachGaveUp = false;
    /** True after the mic encoder has started once. Distinguishes "not ready yet" from "track ended". */
    private _micWasLive = false;
    /** getUserMedia fallback when Twilio's input track was stopped (hold disconnect). */
    private _recoveredMicTrack: MediaStreamTrack | null = null;
    private _ownedMicStream: MediaStream | null = null;
    private _micReacquireInFlight = false;
    private _lastMicReacquireAt = 0;
    private _iceHandlerAttached = false;
    private _bytesLogTimer: ReturnType<typeof setInterval> | null = null;
    private _micBytesWindow = 0;
    private _phoneBytesWindow = 0;
    private _micBytesTotal = 0;
    private _phoneBytesTotal = 0;
    /** Bytes actually written to the current WebSocket (not merely encoded). */
    private _micBytesSentWindow = 0;
    private _phoneBytesSentWindow = 0;
    private _micBytesSentTotal = 0;
    private _phoneBytesSentTotal = 0;
    /**
     * Set when the RTASP socket drops mid-call. Cleared on READY after we force-restart mic/phone
     * so the new server session receives fresh `stream_mic` / `stream_phone` (server does not auto-bind).
     */
    private _needsFullAudioRestartOnReady = false;
    /** Ignore late opus-recorder pages after the encoder that produced them was torn down. */
    private _micEncoderEpoch = 0;
    private _phoneEncoderEpoch = 0;
    /** Pages sent and not yet CONTINUEd. Capped at MAX_PAGES_IN_FLIGHT. */
    private _sendInFlight = 0;
    private _micChunkSeq = 0;
    private _phoneChunkSeq = 0;

    private _rtaspUrlHost = (): string => {
        const u = store.getState().auth?.authUser?.rtaspUrl as string | undefined;
        if (!u) return 'missing';
        try {
            return new URL(u).host;
        } catch {
            return 'unparseable';
        }
    };

    /**
     * Structured fields to grep by `callRecordId` or `callSid` in support / insights.
     */
    private _getPeerConnection = (): RTCPeerConnection | undefined => {
        const c = this._streamConnection?._mediaHandler;
        if (!c) return undefined;
        return (c as { _pc?: RTCPeerConnection; peerConnection?: RTCPeerConnection })._pc
            || (c as { peerConnection?: RTCPeerConnection }).peerConnection;
    };

    private _webrtcPcConnectionState = (): string | undefined => {
        return this._getPeerConnection()?.connectionState;
    };

    /** Prefer Twilio public API, then pcStream, then live audio receivers (ICE restart handover). */
    private _resolveRemoteMediaStream = (): MediaStream | null => {
        const call = this._streamConnection;
        if (!call) return null;
        try {
            if (typeof call.getRemoteStream === 'function') {
                const remote = call.getRemoteStream();
                if (remote instanceof MediaStream && remote.getAudioTracks().some((t: MediaStreamTrack) => t.readyState === 'live')) {
                    return remote;
                }
            }
        } catch {
            /* fall through */
        }
        const pcStream = call._mediaHandler?.pcStream;
        if (pcStream instanceof MediaStream && pcStream.getAudioTracks().some((t: MediaStreamTrack) => t.readyState === 'live')) {
            return pcStream;
        }
        const pc = this._getPeerConnection();
        if (pc && typeof pc.getReceivers === 'function') {
            const live = pc.getReceivers()
                .map((r) => r.track)
                .filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live');
            if (live.length > 0) {
                return new MediaStream(live);
            }
        }
        if (pcStream instanceof MediaStream) {
            return pcStream;
        }
        return null;
    };

    /**
     * Live local mic only. `getLocalStream()` can keep an ended track after Twilio `replaceTrack`s
     * the one actually being sent; that sender track is the one both parties are on.
     */
    private _liveLocalMicCandidates = (): { track: MediaStreamTrack; source: string }[] => {
        const call = this._streamConnection;
        const found: { track: MediaStreamTrack; source: string }[] = [];
        const push = (track: MediaStreamTrack | null | undefined, source: string) => {
            if (!track || track.kind !== 'audio' || track.readyState !== 'live') return;
            if (found.some((c) => c.track === track)) return;
            found.push({ track, source });
        };
        const pushStream = (stream: MediaStream | null | undefined, source: string) => {
            if (!(stream instanceof MediaStream)) return;
            for (const track of stream.getAudioTracks?.() ?? []) push(track, source);
        };
        try {
            if (typeof call?.getLocalStream === 'function') {
                pushStream(call.getLocalStream(), 'getLocalStream');
            }
        } catch {
            /* fall through */
        }
        pushStream(call?._mediaHandler?.stream, 'mediaHandler');
        const pc = this._getPeerConnection();
        if (pc && typeof pc.getSenders === 'function') {
            try {
                for (const sender of pc.getSenders()) push(sender.track, 'sender');
            } catch {
                /* no-op */
            }
        }
        push(this._recoveredMicTrack, 'reacquire');
        return found;
    };

    /** Keep the track we already encode while it is live. Otherwise prefer the outbound sender track. */
    private _pickLiveMicTrack = (): { track: MediaStreamTrack; source: string } | null => {
        const candidates = this._liveLocalMicCandidates();
        if (candidates.length === 0) return null;
        if (this._micTrack && this._micTrack.readyState === 'live') {
            const current = candidates.find((c) => c.track === this._micTrack);
            if (current) return current;
        }
        return candidates.find((c) => c.source === 'sender' && !c.track.muted)
            ?? candidates.find((c) => !c.track.muted)
            ?? candidates.find((c) => c.source === 'sender')
            ?? candidates[0];
    };

    private _describeLocalAudio = (): Record<string, string> => {
        const call = this._streamConnection;
        const describe = (stream: MediaStream | null | undefined): string => {
            if (!(stream instanceof MediaStream)) return 'none';
            const tracks = stream.getAudioTracks?.() ?? [];
            if (tracks.length === 0) return 'empty';
            return tracks.map((t) => `${t.readyState}${t.muted ? ':muted' : ''}`).join(',');
        };
        let localStreamTracks = 'none';
        try {
            if (typeof call?.getLocalStream === 'function') {
                localStreamTracks = describe(call.getLocalStream());
            }
        } catch {
            localStreamTracks = 'error';
        }
        let senderTracks = 'none';
        const pc = this._getPeerConnection();
        if (pc && typeof pc.getSenders === 'function') {
            try {
                const audio = pc.getSenders()
                    .map((s) => s.track)
                    .filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio');
                senderTracks = audio.length === 0
                    ? 'empty'
                    : audio.map((t) => `${t.readyState}${t.muted ? ':muted' : ''}`).join(',');
            } catch {
                senderTracks = 'error';
            }
        }
        return {
            localStreamTracks,
            mediaHandlerTracks: describe(call?._mediaHandler?.stream),
            senderTracks,
        };
    };

    private _bufferByteLength = (buf: ArrayBuffer | Blob | { byteLength?: number; length?: number } | null | undefined): number => {
        if (!buf) return 0;
        if (typeof (buf as ArrayBuffer).byteLength === 'number') return (buf as ArrayBuffer).byteLength;
        if (typeof (buf as Blob).size === 'number') return (buf as Blob).size;
        if (typeof (buf as { length?: number }).length === 'number') return (buf as { length: number }).length;
        return 0;
    };

    /**
     * opus-recorder calls ondataavailable with a Uint8Array (or ArrayBuffer). Always copy so a
     * reused worker view cannot overwrite a page that is still sitting in `_buffer`.
     */
    private _copyEncoderPage = (msg: any): ArrayBuffer | null => {
        if (!msg) return null;
        if (msg instanceof ArrayBuffer) {
            return msg.byteLength > 0 ? msg.slice(0) : null;
        }
        if (ArrayBuffer.isView(msg)) {
            const view = msg as ArrayBufferView;
            if (view.byteLength <= 0) return null;
            return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
        }
        if (msg.buffer instanceof ArrayBuffer) {
            const offset = typeof msg.byteOffset === 'number' ? msg.byteOffset : 0;
            const length = typeof msg.byteLength === 'number' ? msg.byteLength : msg.buffer.byteLength;
            if (length <= 0) return null;
            return msg.buffer.slice(offset, offset + length) as ArrayBuffer;
        }
        return null;
    };

    /** Self-describing frame so the server can route the page even if `stream_*` texts interleave. */
    private _encodeAudioFrame = (type: RttCommandsEnum, page: ArrayBuffer): ArrayBuffer => {
        const isMic = type === RttCommandsEnum.STREAM_DATA_MIC;
        const seq = isMic
            ? (this._micChunkSeq = (this._micChunkSeq + 1) & 0xffff)
            : (this._phoneChunkSeq = (this._phoneChunkSeq + 1) & 0xffff);
        const out = new Uint8Array(8 + page.byteLength);
        out[0] = 0x52; // R
        out[1] = 0x50; // P
        out[2] = 0x31; // 1
        out[3] = AUDIO_FRAME_VERSION;
        out[4] = isMic ? AUDIO_TRACK_MIC : AUDIO_TRACK_PHONE;
        out[5] = 0;
        out[6] = seq & 0xff;
        out[7] = (seq >> 8) & 0xff;
        out.set(new Uint8Array(page), 8);
        return out.buffer;
    };

    private _dropBufferedChunks = (type: RttCommandsEnum) => {
        if (this._buffer.length === 0) return;
        const kept = this._buffer.filter((item) => item?.type !== type);
        const dropped = this._buffer.length - kept.length;
        this._buffer = kept;
        if (dropped > 0) {
            logger.warn(
                'Rtt: dropped queued pages from a torn-down encoder (prevents mixed Ogg streams)',
                LogTitle,
                this._rttMeta('buffer_drop_encoder_restart', { type, dropped, bufferLen: this._buffer.length })
            );
        }
    };

    private _startBytesSentLogger = () => {
        if (this._bytesLogTimer) return;
        this._bytesLogTimer = setInterval(() => {
            if (this._stopRequested || this._rttStopInProgress) return;
            const micTrack = this._micTrack;
            const phoneTrack = this._phoneTrack;
            logger.info(
                'Rtt: per-track bytes produced (10s window)',
                LogTitle,
                this._rttMeta('track_bytes', {
                    micBytesWindow: this._micBytesWindow,
                    phoneBytesWindow: this._phoneBytesWindow,
                    micBytesTotal: this._micBytesTotal,
                    phoneBytesTotal: this._phoneBytesTotal,
                    micBytesSentWindow: this._micBytesSentWindow,
                    phoneBytesSentWindow: this._phoneBytesSentWindow,
                    micBytesSentTotal: this._micBytesSentTotal,
                    phoneBytesSentTotal: this._phoneBytesSentTotal,
                    micTrackState: micTrack?.readyState ?? 'none',
                    phoneTrackState: phoneTrack?.readyState ?? 'none',
                    micTrackSource: this._micTrackSource,
                    micMuted: micTrack?.muted ?? null,
                    phoneMuted: phoneTrack?.muted ?? null,
                    processing: this._processing,
                })
            );
            this._micBytesWindow = 0;
            this._phoneBytesWindow = 0;
            this._micBytesSentWindow = 0;
            this._phoneBytesSentWindow = 0;
            this._checkMicHealth('track_bytes');
        }, BYTES_LOG_INTERVAL_MS);
    };

    private _stopBytesSentLogger = () => {
        if (this._bytesLogTimer) {
            clearInterval(this._bytesLogTimer);
            this._bytesLogTimer = null;
        }
    };

    private _startMicHealthCheck = () => {
        if (this._micHealthTimer) return;
        this._micHealthTimer = setInterval(() => {
            this._checkMicHealth('mic_health');
        }, MIC_HEALTH_CHECK_MS);
    };

    private _stopMicHealthCheck = () => {
        if (this._micHealthTimer) {
            clearInterval(this._micHealthTimer);
            this._micHealthTimer = null;
        }
    };

    /** Recover when the bound mic track ends without an `ended` event, or the sender track replaces it. */
    private _checkMicHealth = (reason: string) => {
        if (this._stopRequested || this._rttStopInProgress || this._micRebuildTimer || this._micReacquireInFlight) return;
        // Hold / recording-pause keeps gain at 0. Reopening the mic here would leak agent audio.
        if (this._captureMuted) return;
        const preferred = this._pickLiveMicTrack();
        const unhealthy = !this._isMicPipeHealthy();
        const mismatch = !!preferred && preferred.track !== this._micTrack;
        if (!unhealthy && !mismatch) return;
        if (preferred && !preferred.track.enabled) {
            preferred.track.enabled = true;
        }
        // Startup still waits for Twilio's track. Once the mic has been live, an ended track
        // will not come back on its own — reopen it while the call stays open.
        if (!preferred) {
            if (this._micWasLive || this._micReattachGaveUp) {
                void this._reacquireEndedMic(reason);
            } else {
                this._ensureMicAttached(reason);
            }
            return;
        }
        this._micReattachGaveUp = false;
        this._ensureMicAttached(reason);
    };

    private _rttMeta(phase: string, extra?: Record<string, unknown>) {
        const c = this._streamConnection;
        const mh = c?._mediaHandler;
        return {
            phase,
            callRecordId: this._callRecordId,
            hasCallRef: !!c,
            callSid: c?.parameters?.CallSid,
            callStatus: typeof c?.status === 'function' ? c.status() : undefined,
            iceState: mh?._iceState,
            hasPcStream: mh?.pcStream instanceof MediaStream,
            hasLocalStream: mh?.stream instanceof MediaStream,
            audioContextState: this._audioContext?.state,
            wsReadyState: this._websocket?.readyState,
            wsState: wsStateLabel(this._websocket?.readyState),
            rtt: {
                connected: this._connected,
                metadataSent: this._metadataSent,
                processing: this._processing,
                stopRequested: this._stopRequested,
                flushingAfterStop: this._flushingAfterStop,
                encoderTailDone: this._encoderTailDone,
                sendInFlight: this._sendInFlight,
            },
            bufferLen: this._buffer.length,
            bufferMaxWhileDown: BUFFER_MAX_WHILE_WS_DOWN,
            reconnectFailureCount: this._reconnectFailureCount,
            webrtcConnectionState: this._webrtcPcConnectionState(),
            hasMicRecorder: this._micMediaRecorder != null,
            hasPhoneRecorder: this._phoneMediaRecorder != null,
            ...extra,
        };
    }

    private _isWsOpen = (): boolean =>
        this._websocket != null && this._websocket.readyState === WebSocket.OPEN;

    /** Hang-up flush may still handshake and send even though the Twilio call is already completed. */
    private _isFlushingAfterStop = (): boolean =>
        this._flushingAfterStop && !this._stopRequested;

    private _canHandshakeAndSend = (): boolean => {
        if (this._stopRequested) {
            return false;
        }
        if (this._isFlushingAfterStop()) {
            return true;
        }
        return this._isTwilioCallUsableForRtt();
    };

    /**
     * RTT reconnect + metadata is only for an active voice leg. When Twilio has ended the call, or
     * another call became active, or this session’s `Call` is not `open`, we must not open sockets
     * or reconnect — otherwise the next call is affected and we leak background work.
     * Hang-up flush uses `_canHandshakeAndSend` instead so the tail can still leave.
     */
    private _isTwilioCallUsableForRtt = (): boolean => {
        if (this._rttStopInProgress) {
            return false;
        }
        if (this._stopRequested) {
            return false;
        }
        const c = this._streamConnection;
        if (!c) {
            return false;
        }
        const details = TwilioVoiceService.getCallDetailsFromConnection(c);
        if (!details?.callRecordId || details.callRecordId !== this._callRecordId) {
            return false;
        }
        if (typeof c.status === 'function') {
            const st = String(c.status() || 'closed').toLowerCase();
            if (['closed', 'cancelled', 'canceled', 'rejected', 'busy', 'no-answer', 'failed', 'completed'].some((f) => st === f)) {
                return false;
            }
        }
        const staticConn = TwilioVoiceService.callConnection;
        if (staticConn && staticConn !== c) {
            return false;
        }
        return true;
    };

    private _reconnectDelayMs(attempt: number) {
        return Math.min(
            RECONNECT_BASE_MS * Math.pow(2, Math.min(attempt, 8)) + Math.random() * 200,
            RECONNECT_MAX_DELAY_MS
        );
    }

    /** If WebSocket is down, trim buffered audio chunks to avoid OOM. */
    private _pruneBufferIfStalled() {
        if (this._rttStopInProgress) return 0;
        if (this._isWsOpen() || this._stopRequested) return 0;
        const cap = BUFFER_MAX_WHILE_WS_DOWN;
        if (this._buffer.length <= cap) return 0;
        const drop = this._buffer.length - cap;
        for (let i = 0; i < drop; i++) {
            this._buffer.shift();
        }
        logger.warn(
            'Rtt: buffer over cap (WS not open), dropped old chunks to protect memory',
            LogTitle,
            this._rttMeta('buffer_capped', { dropped: drop, cap, bufferLen: this._buffer.length })
        );
        return drop;
    }

    private _scheduleReconnect = (reason: string) => {
        if (this._rttStopInProgress) return;
        if (this._stopRequested) return;
        if (!this._isTwilioCallUsableForRtt()) {
            logger.info(
                'Rtt: skip WebSocket reconnect (Twilio call not active for this session)',
                LogTitle,
                this._rttMeta('ws_reconnect_skipped', { reason: 'twilio_inactive' })
            );
            return;
        }
        if (this._reconnectTimer) return;
        const delay = this._reconnectDelayMs(Math.max(0, this._reconnectFailureCount - 1));
        logger.warn(
            'Rtt: WebSocket will reconnect',
            LogTitle,
            this._rttMeta('ws_reconnect_scheduled', { reason, delayMs: Math.round(delay), afterFailures: this._reconnectFailureCount })
        );
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            if (this._stopRequested || !this._isTwilioCallUsableForRtt()) {
                return;
            }
            this._openWebSocket();
        }, delay);
    };

    /**
     * Replace or create WebSocket, wire handlers, resend metadata on open to replay server handshake.
     */
    private _openWebSocket = () => {
        if (this._stopRequested) return;
        if (!this._canHandshakeAndSend()) {
            logger.info('Rtt: skip WebSocket (re)open — no active Twilio call for this RTT session', LogTitle, this._rttMeta('ws_open_skipped', { reason: 'twilio_inactive' }));
            return;
        }
        this._rtaspUrl = (store.getState().auth?.authUser?.rtaspUrl as string) || this._rtaspUrl;
        if (!this._rtaspUrl) {
            logger.error('Rtt: no rtaspUrl, cannot (re)connect', LogTitle, this._rttMeta('ws_no_url'));
            return;
        }
        this._replacePreviousSocket();
        this._metadataSent = false;
        this._processing = false;
        this._connected = false;
        this._websocket = new WebSocket(this._rtaspUrl);
        this._websocket.binaryType = 'blob';
        this._websocket.onmessage = this._websocketCallback;
        this._websocket.onerror = (err) => {
            const ev = err as Event;
            const ws = this._websocket;
            logger.error(
                'Rtt: WebSocket error',
                LogTitle,
                this._rttMeta('ws_error', {
                    eventType: ev?.type || 'error',
                    targetReady: ws != null ? wsStateLabel(ws.readyState) : 'no_socket',
                })
            );
            // Browsers do not require this after `error` — `onclose` usually follows — but explicitly
            // start a clean close from the client so the socket is not left half-open in edge runtimes.
            this._nudgeWebSocketClientClose(1000, RttCommandsEnum.DISCONNECT);
        };
        this._websocket.onopen = () => {
            if (this._stopRequested || !this._canHandshakeAndSend()) {
                this._reconnectFailureCount = 0;
                this._connected = false;
                try {
                    this._websocket?.close(1000, RttCommandsEnum.DISCONNECT);
                } catch {
                    /* no-op */
                }
                logger.info('Rtt: WebSocket onopen ignored (call ended or different call)', LogTitle, this._rttMeta('ws_open_aborted'));
                return;
            }
            this._reconnectFailureCount = 0;
            this._connected = true;
            logger.info('Rtt: WebSocket (re)open, (re)sending metadata', LogTitle, this._rttMeta('ws_open'));
            this._sendMetadata();
        };
        this._websocket.onclose = this._onWebSocketClose;
    };

    private _replacePreviousSocket = () => {
        const prev = this._websocket;
        if (!prev) return;
        prev.onmessage = null;
        prev.onerror = null;
        prev.onopen = null;
        const noop = () => { /* stale socket, ignore (no reconnect loop) */ };
        prev.onclose = noop;
        if (prev.readyState === WebSocket.OPEN || prev.readyState === WebSocket.CONNECTING) {
            try {
                prev.close(WS_CLOSE_RECONNECT, WS_CLOSE_RECONNECT_REASON);
            } catch {
                /* no-op */
            }
        }
        this._websocket = null;
    };

    /**
     * If the server closed the connection, the browser is already in CLOSED — you cannot "close" again
     * meaningfully, but you must still detach listeners and `this._websocket` so the client does not
     * hold a dead socket. If we still have OPEN/CONNECTING (e.g. after onerror), we close from the client.
     */
    private _nudgeWebSocketClientClose = (code: number, reason: string) => {
        const w = this._websocket;
        if (!w) {
            return;
        }
        if (w.readyState === WebSocket.OPEN || w.readyState === WebSocket.CONNECTING) {
            try {
                w.close(code, reason);
            } catch {
                /* no-op */
            }
        }
    };

    /** Run after `onclose` so regardless of who initiated the close, our references are clean. */
    private _clearWebSocketRefAfterClose = () => {
        const w = this._websocket;
        if (!w) {
            return;
        }
        w.onmessage = null;
        w.onerror = null;
        w.onopen = null;
        w.onclose = null;
        this._websocket = null;
    };

    private _onWebSocketClose = (event: CloseEvent) => {
        try {
            this._connected = false;
            this._metadataSent = false;
            this._processing = false;
            this._sendInFlight = 0;
            const ice = this._streamConnection?._mediaHandler?._iceState;
            const webrtcPc = this._webrtcPcConnectionState();

            if (this._isFlushingAfterStop()) {
                logger.info(
                    'Rtt: WebSocket closed while flushing tail',
                    LogTitle,
                    this._rttMeta('ws_close_during_drain', {
                        closeCode: event.code,
                        closedBy: event.wasClean ? 'clean' : 'unclean',
                        bufferRemaining: this._buffer.length,
                    })
                );
                if (this._buffer.length > 0 || !this._encoderTailDone) {
                    this._scheduleFlushReconnect(`close_${event.code}_flush`);
                } else {
                    this._completeRttStopTearDown();
                }
                return;
            }

            if (this._stopRequested) {
                if (this._reconnectTimer) {
                    clearTimeout(this._reconnectTimer);
                    this._reconnectTimer = null;
                }
                logger.info(
                    'Rtt: WebSocket closed (call end / intentional)',
                    LogTitle,
                    this._rttMeta('ws_close_intentional', {
                        closeCode: event.code,
                        reason: event.reason || '',
                        wasClean: event.wasClean,
                        iceState: ice,
                        webrtcConnectionState: webrtcPc,
                    })
                );
                return;
            }

            if (!this._isTwilioCallUsableForRtt()) {
                if (this._reconnectTimer) {
                    clearTimeout(this._reconnectTimer);
                    this._reconnectTimer = null;
                }
                logger.info('Rtt: WebSocket closed but Twilio call is over — no reconnect', LogTitle, this._rttMeta('ws_close_no_reconnect', { closeCode: event.code }));
                return;
            }

            this._reconnectFailureCount += 1;
            if (RECONNECT_MAX_ATTEMPTS > 0 && this._reconnectFailureCount > RECONNECT_MAX_ATTEMPTS) {
                logger.error(
                    'Rtt: max WebSocket reconnection attempts reached; audio may be incomplete for this call',
                    LogTitle,
                    this._rttMeta('ws_reconnect_giveup', { closeCode: event.code, attempts: this._reconnectFailureCount, cap: RECONNECT_MAX_ATTEMPTS })
                );
                return;
            }
            logger.warn(
                'Rtt: WebSocket closed (server or network; will reconnect if call still up)',
                LogTitle,
                this._rttMeta('ws_close', {
                    closeCode: event.code,
                    reason: event.reason || '',
                    wasClean: event.wasClean,
                    iceState: ice,
                    webrtcConnectionState: webrtcPc,
                })
            );
            this._scheduleReconnect(`close_${event.code}`);
            this._needsFullAudioRestartOnReady = true;
        } finally {
            this._clearWebSocketRefAfterClose();
        }
    };

    /**
     * @param twilioCall - Twilio Voice `Call` for this `callRecordId` when known (e.g. from `accept`); avoids racing on static
     *  `TwilioVoiceService.callConnection` at construction.
     */
    constructor(callRecordId: string, twilioCall?: any) {
        this._callRecordId = callRecordId;
        this._streamConnection = twilioCall ?? TwilioVoiceService.callConnection;
        this._audioContext = TwilioDevice.audioContext;
        this._encoderOptions = {
            numberOfChannels: 1,
            encoderPath: '/assets/scripts/encoderWorker.min.js',
            streamPages: true,
            encoderBitRate: 128000,
            originalSampleRateOverride: 48000,
            encoderSampleRate: 48000,
            recordingGain: 1,
        };
        this._camturedBookmark = [];
        const hadParam = twilioCall != null;
        const hadStatic = TwilioVoiceService.callConnection != null;
        logger.info(
            'Rtt: service constructed',
            LogTitle,
            this._rttMeta('construct', { fromConstructorParam: hadParam, hadStaticAtInit: hadStatic })
        );
    }

    start = () => {
        if (!this._streamConnection) {
            this._streamConnection = TwilioVoiceService.callConnection;
            logger.info('Rtt: resynced call ref from static', LogTitle, this._rttMeta('start_resync', { wasMissing: true }));
        }
        logger.info('Rtt: start (mic + phone + WebSocket)', LogTitle, {
            ...this._rttMeta('start', { rtaspHost: this._rtaspUrlHost() }),
        });
        this._ensureMicAttached('start');
        this._processPhoneStream();
        this._startBytesSentLogger();
        this._startMicHealthCheck();

        this._rtaspUrl = (store.getState().auth?.authUser?.rtaspUrl as string) || '';
        this._openWebSocket();
    };

    /**
     * Twilio ICE restart finished — re-acquire remote (and local) tracks and rebuild the recording graph.
     * Twilio's own playback follows the new track; without this, phone pipe stays bound to an ended track.
     */
    reattachMediaAfterTwilioReconnect = (reason: string = 'twilio_reconnected') => {
        if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
            return;
        }
        this._streamConnection = this._streamConnection ?? TwilioVoiceService.callConnection;
        this._audioContext = TwilioDevice.audioContext ?? this._audioContext;
        const staleTrack = this._phoneTrack;
        logger.info(
            'Rtt: re-attaching media after Twilio reconnect',
            LogTitle,
            this._rttMeta('media_reattach', { reason, staleTrackState: staleTrack?.readyState ?? 'none', staleTrackMuted: staleTrack?.muted ?? null })
        );
        this._ensureMicAttached('twilio_reconnect');
        // Force phone rebuild — the cached stream may still expose the pre-restart track, which stays
        // `live` while producing nothing, so wait for a genuinely different track before settling.
        this._teardownPhoneEncoder();
        this._schedulePhoneRebuild(reason, 0, staleTrack);
        this._attachPeerConnectionStateLog();
    };

    muteStreams = () => {
        this._captureMuted = true;
        if (this._micMediaRecorder !== null) {
            //@ts-ignore
            this._micMediaRecorder.setRecordingGain(0);
        }

        if (this._phoneMediaRecorder !== null) {
            //@ts-ignore
            this._phoneMediaRecorder.setRecordingGain(0);
        }
    };

    unmuteStreams = () => {
        this._captureMuted = false;
        if (this._micMediaRecorder !== null) {
            //@ts-ignore
            this._micMediaRecorder.setRecordingGain(1);
        }

        if (this._phoneMediaRecorder !== null) {
            //@ts-ignore
            this._phoneMediaRecorder.setRecordingGain(1);
        }
    };

    didReceiveCallHoldUpdate = (callRecordId: string, isCallHold: boolean) => {
        logger.info('Rtt: call hold / resume for streaming', LogTitle, this._rttMeta('hold', { callRecordId, isCallHold }));

        if (isCallHold) {
            this.muteStreams();
        } else {
            this._streamConnection = TwilioVoiceService.callConnection;
            this._audioContext = TwilioDevice.audioContext;
            this._removePeerStateListener();
            this._iceHandlerAttached = false;
            this._micReattachGaveUp = false;

            this.unmuteStreams();
            for (const candidate of this._liveLocalMicCandidates()) {
                if (!candidate.track.enabled) candidate.track.enabled = true;
            }
            this._ensureMicAttached('hold_resume');
            this._processPhoneStream();
            // Hold disconnect stops the device input track. A disabled track is re-enabled above;
            // an ended one will not produce bytes until we open a new stream.
            if (!this._isMicPipeHealthy() && !this._pickLiveMicTrack()) {
                void this._reacquireEndedMic('hold_resume');
            }
        }
    };

    stop = () => {
        if (this._rttStopInProgress || this._stopRequested) {
            return;
        }
        this._rttStopInProgress = true;
        this._flushingAfterStop = true;
        this._encoderTailDone = false;
        this._flushReconnectCount = 0;
        this._socketDrainWaitedMs = 0;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._phoneRebuildTimer) {
            clearTimeout(this._phoneRebuildTimer);
            this._phoneRebuildTimer = null;
        }
        if (this._micRebuildTimer) {
            clearTimeout(this._micRebuildTimer);
            this._micRebuildTimer = null;
        }
        this._stopBytesSentLogger();
        this._stopMicHealthCheck();
        this._clearPostStopTimers();
        (this._waitAndRetry as DebouncedSend).clear();

        // Stop producing, but keep queued pages and the current encoder epoch so the last
        // opus-recorder `ondataavailable` can still land in `_buffer`.
        this._teardownPhoneEncoder(true);
        this._teardownMicEncoder(true);
        this._stopOwnedMicStream();

        logger.info(
            'Rtt: stop — flushing remaining audio (CONTINUE credit, then close)',
            LogTitle,
            this._rttMeta('stop_flush_begin', { bufferLen: this._buffer.length, sendInFlight: this._sendInFlight })
        );

        this._rttPostStopDrainTimer = setTimeout(() => {
            this._rttPostStopDrainTimer = null;
            if (this._stopRequested) {
                return;
            }
            this._encoderTailDone = true;
            this._micEncoderEpoch += 1;
            this._phoneEncoderEpoch += 1;
            this._beginPostStopFlush();
        }, STOP_RTT_ENCODER_TAIL_MS);

        this._flushDeadlineTimer = setTimeout(() => {
            this._flushDeadlineTimer = null;
            if (this._stopRequested) {
                return;
            }
            logger.warn(
                'Rtt: hang-up flush timed out — closing with remainder',
                LogTitle,
                this._rttMeta('stop_flush_timeout', { bufferRemaining: this._buffer.length, sendInFlight: this._sendInFlight })
            );
            this._completeRttStopTearDown();
        }, STOP_RTT_FLUSH_MAX_MS);
    };

    private _clearPostStopTimers = () => {
        if (this._rttPostStopDrainTimer) {
            clearTimeout(this._rttPostStopDrainTimer);
            this._rttPostStopDrainTimer = null;
        }
        if (this._flushDeadlineTimer) {
            clearTimeout(this._flushDeadlineTimer);
            this._flushDeadlineTimer = null;
        }
        if (this._socketDrainTimer) {
            clearTimeout(this._socketDrainTimer);
            this._socketDrainTimer = null;
        }
    };

    /** After the encoder tail window: credit-send remaining pages, reconnect once if needed, then close. */
    private _beginPostStopFlush = () => {
        if (this._stopRequested || !this._flushingAfterStop) {
            return;
        }
        if (this._isWsOpen()) {
            if (!this._processing && !this._metadataSent) {
                this._sendMetadata();
            }
            this._sendBuffer();
            this._tryFinishPostStopFlush();
            return;
        }
        if (this._buffer.length > 0) {
            this._scheduleFlushReconnect('stop_ws_down');
            return;
        }
        this._tryFinishPostStopFlush();
    };

    private _scheduleFlushReconnect = (reason: string) => {
        if (this._stopRequested || !this._flushingAfterStop) {
            return;
        }
        if (this._isWsOpen() || this._websocket?.readyState === WebSocket.CONNECTING) {
            return;
        }
        if (this._reconnectTimer) {
            return;
        }
        if (this._buffer.length === 0 && this._encoderTailDone) {
            this._tryFinishPostStopFlush();
            return;
        }
        if (this._flushReconnectCount >= FLUSH_RECONNECT_MAX_ATTEMPTS) {
            logger.warn(
                'Rtt: flush reconnect attempts exhausted — remaining audio will not be sent',
                LogTitle,
                this._rttMeta('stop_flush_reconnect_giveup', { reason, bufferRemaining: this._buffer.length })
            );
            this._completeRttStopTearDown();
            return;
        }
        const delay = this._flushReconnectCount === 0
            ? 0
            : Math.min(this._reconnectDelayMs(this._flushReconnectCount), 1_500);
        logger.warn(
            'Rtt: WebSocket will reconnect to flush remaining audio after hang-up',
            LogTitle,
            this._rttMeta('stop_flush_reconnect_scheduled', {
                reason,
                delayMs: Math.round(delay),
                attempt: this._flushReconnectCount + 1,
                bufferLen: this._buffer.length,
            })
        );
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            if (this._stopRequested || !this._flushingAfterStop) {
                return;
            }
            this._flushReconnectCount += 1;
            this._openWebSocket();
        }, delay);
    };

    private _tryFinishPostStopFlush = () => {
        if (this._stopRequested || !this._flushingAfterStop || !this._encoderTailDone) {
            return;
        }
        if (this._buffer.length > 0 || this._sendInFlight) {
            return;
        }
        this._waitForBufferedAmountThenTearDown();
    };

    private _waitForBufferedAmountThenTearDown = () => {
        if (this._stopRequested) {
            return;
        }
        const ws = this._websocket;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            this._completeRttStopTearDown();
            return;
        }
        if (this._buffer.length > 0 || this._sendInFlight) {
            return;
        }
        if (ws.bufferedAmount > 0 && this._socketDrainWaitedMs < STOP_RTT_SOCKET_DRAIN_MAX_MS) {
            this._socketDrainTimer = setTimeout(() => {
                this._socketDrainTimer = null;
                this._socketDrainWaitedMs += STOP_RTT_SOCKET_DRAIN_POLL_MS;
                this._waitForBufferedAmountThenTearDown();
            }, STOP_RTT_SOCKET_DRAIN_POLL_MS);
            return;
        }
        if (ws.bufferedAmount > 0) {
            logger.warn(
                'Rtt: socket still has unsent bytes after drain wait — closing anyway',
                LogTitle,
                this._rttMeta('stop_socket_drain_timeout', { bufferedAmount: ws.bufferedAmount })
            );
        } else {
            logger.info(
                'Rtt: hang-up flush complete — closing socket',
                LogTitle,
                this._rttMeta('stop_flush_complete')
            );
        }
        this._completeRttStopTearDown();
    };

    private _completeRttStopTearDown = () => {
        if (this._stopRequested) {
            return;
        }
        this._stopRequested = true;
        this._flushingAfterStop = false;
        this._clearPostStopTimers();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        (this._waitAndRetry as DebouncedSend).clear();
        this._tearingDownMediaHandlers();
        this._tearingDownWebSocket();
        this._removePeerStateListener();
        if (this._buffer.length > 0) {
            logger.warn(
                'Rtt: discarding buffer after stop (not sent to server)',
                LogTitle,
                this._rttMeta('stop_buffer_unsent', { bufferRemaining: this._buffer.length })
            );
        }
        this._buffer = [];
        this._connected = false;
        this._metadataSent = false;
        this._processing = false;
        this._reconnectFailureCount = 0;
        this._flushReconnectCount = 0;
        this._encoderTailDone = false;
        this._streamConnection = null;
        this._iceHandlerAttached = false;
        this._needsFullAudioRestartOnReady = false;
        this._micBytesWindow = 0;
        this._phoneBytesWindow = 0;
        this._micBytesTotal = 0;
        this._phoneBytesTotal = 0;
        this._micBytesSentWindow = 0;
        this._phoneBytesSentWindow = 0;
        this._micBytesSentTotal = 0;
        this._phoneBytesSentTotal = 0;
        this._sendInFlight = 0;
        this._micChunkSeq = 0;
        this._phoneChunkSeq = 0;
        this._rttStopInProgress = false;
        logger.info('Rtt: stop — RTT session fully cleared for next call', LogTitle, this._rttMeta('stop_final'));
    };

    private _tearingDownWebSocket = () => {
        const w = this._websocket;
        if (!w) return;
        w.onmessage = null;
        w.onerror = null;
        w.onopen = null;
        w.onclose = null;
        if (w.readyState === WebSocket.OPEN || w.readyState === WebSocket.CONNECTING) {
            try {
                w.close(1000, RttCommandsEnum.DISCONNECT);
            } catch {
                /* no-op */
            }
        }
        this._websocket = null;
    };

    private _tearingDownMediaHandlers = () => {
        try {
            const mh = this._streamConnection?._mediaHandler;
            if (mh) {
                mh.oniceconnectionstatechange = null;
            }
        } catch {
            /* no-op */
        }
    };

    private _removePeerStateListener = () => {
        if (this._pcForPeerLog && this._peerStateLogHandler) {
            try {
                this._pcForPeerLog.removeEventListener('connectionstatechange', this._peerStateLogHandler);
            } catch {
                /* no-op */
            }
        }
        if (this._pcForPeerLog && this._peerTrackHandler) {
            try {
                this._pcForPeerLog.removeEventListener('track', this._peerTrackHandler);
            } catch {
                /* no-op */
            }
        }
        this._pcForPeerLog = null;
        this._peerStateLogHandler = null;
        this._peerTrackHandler = null;
        this._peerStateListenerAttached = false;
    };

    createManualBookmark = (startTime: number, endTime: number) => {
        const bookmarkMarker = { id: uuidv4(), startTime, endTime };
        logger.info('Rtt: manual bookmark queued', LogTitle, this._rttMeta('manual_bookmark', { bookmarkId: bookmarkMarker.id, startTime, endTime }));

        this._buffer.push({
            type: RttCommandsEnum.MANUAL_BOOKMARK,
            buffer: bookmarkMarker,
        });

        // Define types for AddBookmarkFeature and store.dispatch if not already done.
        // store.dispatch(AddBookmarkFeature.Actions.trackManualBookmark(this._callRecordId, bookmarkMarker));
    };

    private _unbindPhoneTrackWatchers = () => {
        if (this._phoneTrack) {
            if (this._phoneTrackEndedHandler) {
                this._phoneTrack.removeEventListener('ended', this._phoneTrackEndedHandler);
            }
            if (this._phoneTrackMuteHandler) {
                this._phoneTrack.removeEventListener('mute', this._phoneTrackMuteHandler);
            }
        }
        this._phoneTrackEndedHandler = null;
        this._phoneTrackMuteHandler = null;
        this._phoneTrack = null;
    };

    private _unbindMicTrackWatchers = () => {
        if (this._micTrack && this._micTrackEndedHandler) {
            this._micTrack.removeEventListener('ended', this._micTrackEndedHandler);
        }
        this._micTrackEndedHandler = null;
        this._micTrack = null;
        this._micTrackSource = null;
    };

    private _teardownPhoneEncoder = (keepQueuedPages = false) => {
        if (!keepQueuedPages) {
            this._phoneEncoderEpoch += 1;
            this._dropBufferedChunks(RttCommandsEnum.STREAM_DATA_PHONE);
        }
        this._unbindPhoneTrackWatchers();
        try {
            //@ts-ignore
            this._phoneStreamSource?.disconnect();
        } catch {
            /* no-op */
        }
        try {
            //@ts-ignore
            this._phoneMediaRecorder?.stop();
        } catch {
            /* no-op */
        }
        this._phoneStreamSource = null;
        this._phoneMediaRecorder = null;
    };

    private _teardownMicEncoder = (keepQueuedPages = false) => {
        if (!keepQueuedPages) {
            this._micEncoderEpoch += 1;
            this._dropBufferedChunks(RttCommandsEnum.STREAM_DATA_MIC);
        }
        this._unbindMicTrackWatchers();
        try {
            //@ts-ignore
            this._micStreamSource?.disconnect();
        } catch {
            /* no-op */
        }
        try {
            //@ts-ignore
            this._micMediaRecorder?.stop();
        } catch {
            /* no-op */
        }
        this._micStreamSource = null;
        this._micMediaRecorder = null;
    };

    private _isPhonePipeHealthy = (): boolean =>
        !!this._phoneMediaRecorder && !!this._phoneTrack && this._phoneTrack.readyState !== 'ended';

    private _isMicPipeHealthy = (): boolean =>
        !!this._micMediaRecorder && !!this._micTrack && this._micTrack.readyState === 'live';

    /**
     * Twilio can stop the getUserMedia track as soon as the outbound sender exists, and the `ended`
     * event does not always arrive. Retry until a live local or sender track is there to encode.
     */
    private _scheduleMicRebuild = (reason: string, attempt: number = 0) => {
        if (this._stopRequested || this._rttStopInProgress) return;
        if (attempt === 0) {
            this._micReattachGaveUp = false;
        }
        if (this._micRebuildTimer) {
            clearTimeout(this._micRebuildTimer);
        }
        const delay = attempt === 0 ? MIC_REBUILD_DEBOUNCE_MS : MIC_REBUILD_RETRY_MS;
        const isLastAttempt = attempt + 1 >= MIC_REBUILD_MAX_ATTEMPTS;
        this._micRebuildTimer = setTimeout(() => {
            this._micRebuildTimer = null;
            if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
                return;
            }
            this._startMicrophoneStream(attempt === 0 ? reason : `${reason}_retry${attempt}`);
            if (this._isMicPipeHealthy()) {
                return;
            }
            if (isLastAttempt) {
                this._micReattachGaveUp = true;
                logger.error(
                    'Rtt: gave up re-attaching mic track — agent audio will be missing',
                    LogTitle,
                    this._rttMeta('mic_reattach_giveup', { reason, attempts: attempt + 1, ...this._describeLocalAudio() })
                );
                return;
            }
            this._scheduleMicRebuild(reason, attempt + 1);
        }, delay);
    };

    private _stopOwnedMicStream = () => {
        const stream = this._ownedMicStream;
        this._ownedMicStream = null;
        this._recoveredMicTrack = null;
        if (!stream) return;
        stream.getTracks().forEach((track) => {
            try { track.stop(); } catch { /* already ended */ }
        });
    };

    private _replaceEndedSenderTrack = async (track: MediaStreamTrack) => {
        const pc = this._getPeerConnection();
        if (!pc || typeof pc.getSenders !== 'function') return;
        let sender: RTCRtpSender | undefined;
        try {
            sender = pc.getSenders().find((s) => !s.track || (s.track.kind === 'audio' && s.track.readyState !== 'live'));
        } catch {
            return;
        }
        if (!sender || sender.track === track) return;
        try {
            await sender.replaceTrack(track);
            logger.info(
                'Rtt: replaced ended outbound mic track',
                LogTitle,
                this._rttMeta('mic_sender_replaced', { micTrackState: track.readyState })
            );
        } catch (err) {
            logger.error(
                'Rtt: failed to replace ended outbound mic track',
                LogTitle,
                this._rttMeta('mic_sender_replace_failed', { message: err instanceof Error ? err.message : String(err) })
            );
        }
    };

    /**
     * Hold disconnect calls track.stop() on the device input, so readyState stays `ended`
     * and the recorder keeps emitting silence. Open a new mic and bind it while the call is open.
     */
    private _reacquireEndedMic = async (reason: string) => {
        if (this._stopRequested || this._rttStopInProgress || this._captureMuted || this._micReacquireInFlight) return;
        if (!this._isTwilioCallUsableForRtt()) return;
        if (this._pickLiveMicTrack()) {
            this._micReattachGaveUp = false;
            this._ensureMicAttached(reason);
            return;
        }
        const now = Date.now();
        if (reason !== 'hold_resume' && now - this._lastMicReacquireAt < MIC_REACQUIRE_COOLDOWN_MS) return;
        this._micReacquireInFlight = true;
        this._lastMicReacquireAt = now;
        if (this._micRebuildTimer) {
            clearTimeout(this._micRebuildTimer);
            this._micRebuildTimer = null;
        }
        try {
            logger.warn(
                'Rtt: mic track ended during open call — reacquiring microphone',
                LogTitle,
                this._rttMeta('mic_reacquire', { reason, ...this._describeLocalAudio() })
            );
            if (this._micTrack && this._micTrack.readyState !== 'live') {
                this._teardownMicEncoder();
            }
            const recovered = await TwilioVoiceService.recoverMicrophoneInput(reason);
            if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
                if (recovered.owned) {
                    recovered.stream?.getTracks().forEach((track) => {
                        try { track.stop(); } catch { /* already ended */ }
                    });
                }
                return;
            }
            const track = recovered.stream?.getAudioTracks().find((t) => t.readyState === 'live') ?? null;
            if (track) {
                if (!track.enabled) track.enabled = true;
                if (this._ownedMicStream && this._ownedMicStream !== recovered.stream) {
                    this._stopOwnedMicStream();
                }
                if (recovered.owned) {
                    this._ownedMicStream = recovered.stream;
                }
                this._recoveredMicTrack = track;
                await this._replaceEndedSenderTrack(track);
            }
            this._micReattachGaveUp = false;
            this._ensureMicAttached(reason === 'hold_resume' ? 'hold_resume' : 'mic_reacquire');
        } finally {
            this._micReacquireInFlight = false;
        }
    };

    private _ensureMicAttached = (reason: string) => {
        this._startMicrophoneStream(reason);
        if (this._isMicPipeHealthy()) {
            return;
        }
        this._scheduleMicRebuild(reason);
    };

    /**
     * Keep re-checking for the replacement remote track until one attaches. Twilio hands the new
     * `MediaStreamTrack` over some time after `reconnected`, so a single pass leaves the phone pipe silent.
     * `avoidTrack` is the pre-reconnect track: a stale remote track stays `live` (it only goes `muted`),
     * so we must not settle back onto it while a genuine replacement may still be arriving.
     */
    private _schedulePhoneRebuild = (reason: string, attempt: number = 0, avoidTrack: MediaStreamTrack | null = null) => {
        if (this._stopRequested || this._rttStopInProgress) return;
        if (this._phoneRebuildTimer) {
            clearTimeout(this._phoneRebuildTimer);
        }
        const delay = attempt === 0 ? PHONE_REBUILD_DEBOUNCE_MS : PHONE_REBUILD_RETRY_MS;
        const isLastAttempt = attempt + 1 >= PHONE_REBUILD_MAX_ATTEMPTS;
        this._phoneRebuildTimer = setTimeout(() => {
            this._phoneRebuildTimer = null;
            if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
                return;
            }
            // A reconnect often unmutes the same track instead of replacing it. Binding that track
            // immediately avoids ~20s of silence while we wait out PHONE_REBUILD_MAX_ATTEMPTS.
            if (avoidTrack && avoidTrack.readyState === 'live' && !avoidTrack.muted) {
                this._setupPhoneEncoder(`${reason}_recovered`, null);
                if (this._isPhonePipeHealthy()) {
                    return;
                }
            }
            // On the final attempt accept the old track rather than leaving the pipe silent.
            this._setupPhoneEncoder(attempt === 0 ? reason : `${reason}_retry${attempt}`, isLastAttempt ? null : avoidTrack);
            if (this._isPhonePipeHealthy()) {
                return;
            }
            if (isLastAttempt) {
                logger.error(
                    'Rtt: gave up re-attaching phone remote track — phone audio will be missing',
                    LogTitle,
                    this._rttMeta('phone_reattach_giveup', { reason, attempts: attempt + 1 })
                );
                return;
            }
            this._schedulePhoneRebuild(reason, attempt + 1, avoidTrack);
        }, delay);
    };

    private _bindPhoneTrackWatchers = (track: MediaStreamTrack) => {
        this._unbindPhoneTrackWatchers();
        this._phoneTrack = track;
        this._phoneTrackEndedHandler = () => {
            logger.warn('Rtt: phone remote track ended — will rebuild encoder', LogTitle, this._rttMeta('phone_track_ended'));
            this._schedulePhoneRebuild('phone_track_ended');
        };
        this._phoneTrackMuteHandler = () => {
            logger.warn('Rtt: phone remote track muted — will rebuild encoder', LogTitle, this._rttMeta('phone_track_muted'));
            this._schedulePhoneRebuild('phone_track_muted');
        };
        track.addEventListener('ended', this._phoneTrackEndedHandler);
        track.addEventListener('mute', this._phoneTrackMuteHandler);
    };

    private _bindMicTrackWatchers = (track: MediaStreamTrack) => {
        this._unbindMicTrackWatchers();
        this._micTrack = track;
        this._micTrackEndedHandler = () => {
            logger.warn('Rtt: mic track ended — will rebuild encoder', LogTitle, this._rttMeta('mic_track_ended', this._describeLocalAudio()));
            if (this._stopRequested || this._rttStopInProgress || this._captureMuted) return;
            // The ended track will not produce audio again. Re-enable is not enough; open a new one
            // unless Twilio already published a replacement live track.
            void this._reacquireEndedMic('mic_track_ended');
        };
        track.addEventListener('ended', this._micTrackEndedHandler);
    };

    private _setupPhoneEncoder = (reason: string = 'setup', avoidTrack: MediaStreamTrack | null = null) => {
        if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
            return;
        }
        if (!this._streamConnection) {
            logger.error('Rtt: no call ref, phone/remote stream skipped', LogTitle, this._rttMeta('phone_aborted_no_call', { reason }));
            return;
        }
        if (!this._audioContext) {
            this._audioContext = TwilioDevice.audioContext;
        }
        const mediaStream = this._resolveRemoteMediaStream();
        if (!(mediaStream instanceof MediaStream)) {
            logger.error('Rtt: invalid phone remote stream (not a MediaStream yet)', LogTitle, this._rttMeta('phone_invalid', { reason, valueType: 'null' }));
            return;
        }
        const tracks = mediaStream.getAudioTracks?.() ?? [];
        const usable = tracks.filter((t) => t.readyState === 'live' && t !== avoidTrack);
        const liveTrack = usable.find((t) => !t.muted) ?? usable[0];
        if (!liveTrack) {
            logger.warn(
                'Rtt: phone remote track not live yet — deferring attach',
                LogTitle,
                this._rttMeta('phone_track_not_live', {
                    reason,
                    trackCount: tracks.length,
                    trackStates: tracks.map((t) => t.readyState),
                    waitingForNewTrack: !!avoidTrack,
                })
            );
            return;
        }

        // Same live track already feeding the encoder — skip rebuild.
        if (
            this._phoneMediaRecorder
            && this._phoneTrack
            && this._phoneTrack === liveTrack
            && this._phoneTrack.readyState === 'live'
        ) {
            return;
        }

        this._teardownPhoneEncoder();

        const buffer = this._buffer;
        const epoch = this._phoneEncoderEpoch;
        const streamForSource = new MediaStream([liveTrack]);
        //@ts-ignore
        this._phoneStreamSource = this._audioContext?.createMediaStreamSource(streamForSource);
        //@ts-ignore
        this._phoneMediaRecorder = new Recorder(this._encoderOptions);
        //@ts-ignore
        this._phoneMediaRecorder.ondataavailable = (msg: any) => {
            if (epoch !== this._phoneEncoderEpoch) {
                return;
            }
            const page = this._copyEncoderPage(msg ?? msg?.buffer);
            if (!page) {
                return;
            }
            const n = page.byteLength;
            this._phoneBytesWindow += n;
            this._phoneBytesTotal += n;
            buffer.push({
                type: RttCommandsEnum.STREAM_DATA_PHONE,
                buffer: page,
            });
            this._sendBuffer();
        };
        //@ts-ignore
        this._phoneMediaRecorder?.start(this._phoneStreamSource);
        this._bindPhoneTrackWatchers(liveTrack);
        logger.info(
            'Rtt: phone (remote) stream + encoder started',
            LogTitle,
            this._rttMeta('phone_ready', {
                reason,
                phoneTrackCount: tracks.length,
                phoneLabel: liveTrack.label,
                phoneTrackState: liveTrack.readyState,
                phoneMuted: liveTrack.muted,
            })
        );
    };

    _startMicrophoneStream = (reason: string = 'start') => {
        if (this._stopRequested || this._rttStopInProgress) {
            return;
        }
        if (!this._streamConnection) {
            logger.error('Rtt: no Twilio call ref, mic stream skipped', LogTitle, this._rttMeta('mic_aborted_no_call', { reason }));
            return;
        }
        if (!this._audioContext) {
            this._audioContext = TwilioDevice.audioContext;
        }
        const picked = this._pickLiveMicTrack();
        if (!picked) {
            // An ended getLocalStream() track used to be encoded anyway, which is silence for the rest of the call.
            if (this._micTrack && this._micTrack.readyState !== 'live') {
                this._teardownMicEncoder();
            }
            if (!/_retry\d+$/.test(reason)) {
                logger.warn(
                    'Rtt: mic track not live yet — deferring attach',
                    LogTitle,
                    this._rttMeta('mic_track_not_live', { reason, ...this._describeLocalAudio() })
                );
            }
            return;
        }
        if (!picked.track.enabled) {
            picked.track.enabled = true;
        }
        if (
            this._micMediaRecorder
            && this._micTrack === picked.track
            && this._micTrack.readyState === 'live'
        ) {
            return;
        }

        this._teardownMicEncoder();

        const buffer = this._buffer;
        const epoch = this._micEncoderEpoch;
        const streamForSource = new MediaStream([picked.track]);
        try {
            //@ts-ignore
            this._micStreamSource = this._audioContext?.createMediaStreamSource(streamForSource);
            // @ts-ignore
            this._micMediaRecorder = new Recorder(this._encoderOptions);
            // @ts-ignore
            this._micMediaRecorder.ondataavailable = (msg: any) => {
                if (epoch !== this._micEncoderEpoch) {
                    return;
                }
                const page = this._copyEncoderPage(msg ?? msg?.buffer);
                if (!page) {
                    return;
                }
                const n = page.byteLength;
                this._micBytesWindow += n;
                this._micBytesTotal += n;
            buffer.push({
                type: RttCommandsEnum.STREAM_DATA_MIC,
                buffer: page,
            });
            this._sendBuffer();
        };
            // @ts-ignore
            this._micMediaRecorder.start(this._micStreamSource);
            if (this._captureMuted) {
                //@ts-ignore
                this._micMediaRecorder.setRecordingGain(0);
            }
        } catch (err) {
            logger.error(
                'Rtt: mic encoder failed to start',
                LogTitle,
                this._rttMeta('mic_start_failed', { reason, micTrackSource: picked.source, message: err instanceof Error ? err.message : String(err) })
            );
            this._teardownMicEncoder();
            return;
        }
        this._micTrackSource = picked.source;
        this._micWasLive = true;
        this._micReattachGaveUp = false;
        this._bindMicTrackWatchers(picked.track);
        logger.info(
            'Rtt: mic stream + encoder started',
            LogTitle,
            this._rttMeta('mic_ready', {
                reason,
                micTrackSource: picked.source,
                micLabel: picked.track.label,
                micTrackState: picked.track.readyState,
                micMuted: picked.track.muted,
            })
        );
    };

    /**
     * After RTASP handshake (or mid-call), ensure encoders are bound to live tracks.
     * WS reconnect used to complete metadata→READY and never restart a dead audio pump.
     */
    private _ensureAudioPumpsRunning = (reason: string) => {
        if (this._stopRequested || this._rttStopInProgress || !this._isTwilioCallUsableForRtt()) {
            return;
        }
        const micDead = !this._micMediaRecorder || this._micTrack?.readyState === 'ended';
        const phoneDead = !this._phoneMediaRecorder || this._phoneTrack?.readyState === 'ended';
        if (!micDead && !phoneDead) {
            return;
        }
        logger.info(
            'Rtt: ensuring audio pumps after handshake',
            LogTitle,
            this._rttMeta('ensure_pumps', { reason, micDead, phoneDead })
        );
        if (micDead) {
            this._ensureMicAttached(reason);
        }
        if (phoneDead) {
            this._schedulePhoneRebuild(reason);
        }
    };

    _processPhoneStream = () => {
        if (!this._streamConnection) {
            logger.error('Rtt: no call ref, phone/remote stream skipped', LogTitle, this._rttMeta('phone_aborted_no_call'));
            return;
        }

        const mh = this._streamConnection._mediaHandler;
        if (mh && !this._iceHandlerAttached) {
            this._iceHandlerAttached = true;
            // mediaHandler.oniceconnectionstatechange is Twilio's consumer hook (default noop).
            mh.oniceconnectionstatechange = (state: string) => {
                const ice = String(state || '').toLowerCase();
                if (ice === 'connected' || ice === 'completed') {
                    this._schedulePhoneRebuild(`ice_${ice}`);
                    this._ensureMicAttached(`ice_${ice}`);
                } else {
                    const bad = /^(failed|disconnected|closed)$/.test(ice);
                    const iceMeta = this._rttMeta('phone_ice_event', { iceEventState: state, treatedAs: bad ? 'degraded_or_lost' : 'transient' });
                    if (bad) {
                        logger.error('Rtt: phone ICE not connected (callback)', LogTitle, iceMeta);
                    } else {
                        logger.warn('Rtt: phone ICE not connected (callback)', LogTitle, iceMeta);
                    }
                }
            };
        }

        const ice = this._streamConnection._mediaHandler?._iceState;
        if (ice === 'connected' || ice === 'completed') {
            this._setupPhoneEncoder(`ice_already_${ice}`);
            this._ensureMicAttached(`ice_already_${ice}`);
        } else {
            // Try anyway if a live remote track already exists (common right after accept).
            const remote = this._resolveRemoteMediaStream();
            const hasLive = remote?.getAudioTracks?.().some((t) => t.readyState === 'live');
            if (hasLive) {
                this._setupPhoneEncoder('remote_track_early');
            } else {
                logger.warn(
                    'Rtt: phone ICE pending, will attach remote stream on connected',
                    LogTitle,
                    this._rttMeta('phone_ice_wait', { iceState: ice })
                );
            }
        }
        this._attachPeerConnectionStateLog();
    };

    /** `connectionState` (ICE + DTLS) alongside Twilio’s ICE label — gives another signal when the carrier leg drops. */
    private _attachPeerConnectionStateLog = () => {
        const c = this._streamConnection?._mediaHandler;
        if (!c) return;
        const pc = this._getPeerConnection();
        if (!pc || !pc.addEventListener) return;
        // Rebind if Twilio swapped the PeerConnection during ICE restart.
        if (this._peerStateListenerAttached && this._pcForPeerLog === pc) return;
        this._removePeerStateListener();
        this._peerStateListenerAttached = true;
        this._pcForPeerLog = pc;
        this._peerStateLogHandler = () => {
            if (this._stopRequested) {
                return;
            }
            const ice = pc.iceConnectionState;
            const cs = pc.connectionState;
            if (cs === 'connected' && ice === 'connected') {
                this._schedulePhoneRebuild('pc_connected');
                return;
            }
            const isBad = (s: string) => s === 'failed' || s === 'disconnected' || s === 'closed';
            if (isBad(ice) || isBad(cs)) {
                const transient = ice === 'disconnected' || cs === 'disconnected';
                const msg = transient
                    ? 'Rtt: WebRTC peer disconnect (ICE / transport)'
                    : 'Rtt: WebRTC peer failed / closed';
                const webrtcMeta = this._rttMeta('webrtc_state', { connectionState: cs, iceConnectionState: ice });
                if (transient) {
                    logger.warn(msg, LogTitle, webrtcMeta);
                } else {
                    logger.error(msg, LogTitle, webrtcMeta);
                }
            }
        };
        pc.addEventListener('connectionstatechange', this._peerStateLogHandler);

        // Most deterministic handover signal: the replacement receiver arriving after an ICE restart.
        this._peerTrackHandler = (event: RTCTrackEvent) => {
            if (this._stopRequested || this._rttStopInProgress) return;
            if (event.track?.kind !== 'audio') return;
            logger.info(
                'Rtt: new remote audio track from peer connection',
                LogTitle,
                this._rttMeta('remote_track_event', { trackState: event.track.readyState, trackMuted: event.track.muted })
            );
            this._schedulePhoneRebuild('remote_track_event');
        };
        pc.addEventListener('track', this._peerTrackHandler);
    };

    private _emitBufferChunk = (): 'sent' | 'empty' | 'not_open' | 'requeued' => {
        if (!this._isWsOpen() || !this._websocket) {
            return 'not_open';
        }
        const data = this._buffer.shift();
        if (!data) {
            return 'empty';
        }
        try {
            const ws = this._websocket;
            switch (data.type) {
                case RttCommandsEnum.MANUAL_BOOKMARK:
                    ws.send(`${RttCommandsEnum.MANUAL_BOOKMARK} ${JSON.stringify(data.buffer)}`);
                    break;
                default: {
                    // Each chunk re-announces the track on this socket — required after WS reconnect
                    // because the server does not auto-bind mic/phone from the previous session.
                    // The binary is also self-describing (RP1 frame) so a raced second stream_*
                    // cannot deliver this page onto the other pipe.
                    const frame = this._encodeAudioFrame(data.type, data.buffer);
                    ws.send(data.type);
                    ws.send(frame);
                    const n = this._bufferByteLength(data.buffer);
                    if (data.type === RttCommandsEnum.STREAM_DATA_MIC) {
                        this._micBytesSentWindow += n;
                        this._micBytesSentTotal += n;
                    } else if (data.type === RttCommandsEnum.STREAM_DATA_PHONE) {
                        this._phoneBytesSentWindow += n;
                        this._phoneBytesSentTotal += n;
                    }
                    break;
                }
            }
            this._sendInFlight += 1;
            return 'sent';
        } catch (error) {
            this._buffer.unshift(data);
            logger.error(
                'Rtt: failed to send buffer chunk (will retry via debounce)',
                LogTitle,
                this._rttMeta('send_buffer_error', { error: String(error) })
            );
            return 'requeued';
        }
    };

    private _sendBuffer = () => {
        if (this._stopRequested) {
            return;
        }

        while (this._sendInFlight < MAX_PAGES_IN_FLIGHT) {
            if (!this._isWsOpen()) {
                if (this._isFlushingAfterStop()) {
                    this._scheduleFlushReconnect('send_ws_down');
                    return;
                }
                this._pruneBufferIfStalled();
                this._waitAndRetry();
                return;
            }

            // Do not send stream_mic / stream_phone until READY — a fresh WS session has no track binding yet.
            if (!this._processing) {
                return;
            }

            const r = this._emitBufferChunk();
            if (r === 'sent') {
                (this._waitAndRetry as DebouncedSend).clear();
                continue;
            }
            if (r === 'empty') {
                if (this._isFlushingAfterStop()) {
                    this._tryFinishPostStopFlush();
                    return;
                }
                this._waitAndRetry();
                return;
            }
            if (r === 'requeued') {
                this._waitAndRetry();
                return;
            }
            if (r === 'not_open' && this._isFlushingAfterStop()) {
                this._scheduleFlushReconnect('send_not_open');
            }
            return;
        }
    };

    private _sendMetadata = () => {
        if (this._stopRequested || !this._canHandshakeAndSend()) {
            logger.info('Rtt: skip metadata — call not active for RTT', LogTitle, this._rttMeta('metadata_skipped', { reason: 'twilio_inactive' }));
            return;
        }
        if (!this._isWsOpen() || !this._websocket) {
            logger.warn('Rtt: skip metadata — WebSocket not open', LogTitle, this._rttMeta('metadata_skipped', { reason: 'ws_not_open' }));
            return;
        }
        const connection = this._streamConnection ?? TwilioVoiceService.callConnection;

        if (!connection) {
            logger.error('Rtt: cannot send metadata — no Twilio call (instance or static)', LogTitle, this._rttMeta('metadata_no_call'));
            return;
        }

        const user = store.getState()?.auth?.user;
        const tenantCode = store.getState()?.auth?.authUser?.tenantCode;
        const callingContact = store.getState()?.callingContact.data;

        const callDetails = TwilioVoiceService.getCallDetailsFromConnection(connection);
        const isOutgoing = callDetails?.callDirection === CallActivityDirectionEnum.outgoing;
        const correlationToken = isOutgoing ? (connection?.CorrelationToken ?? uuidv4()) : uuidv4();
        let contactName= callDetails?.contactNumber;
    
        if (callingContact?.remoteId) {
           contactName = `${callingContact?.firstName ?? ''} ${callingContact?.lastName ?? ''}`
         } else {
           contactName = 'Unknown';
         }

        const metadata = {
            tenantCode,
            correlationToken,
            callRecordId: this._callRecordId,
            userId: user.id,
            userDisplayName: user?.displayName || `${user.firstName} ${user.lastName}`,
            contactId: callDetails?.contactId,
            contactName: contactName,
            contactNumber: callDetails?.contactNumber,
        };
        
        const metaDataString = JSON.stringify(metadata);
        try {
            this._websocket?.send(`${RttCommandsEnum.METADATA} ${metaDataString}`);
        } catch (e) {
            this._metadataSent = false;
            logger.error('Rtt: send metadata failed (socket)', LogTitle, this._rttMeta('metadata_send_error', { error: String(e) }));
            return;
        }
        this._metadataSent = true;
        logger.info('Rtt: metadata sent to RT server', LogTitle, this._rttMeta('metadata_sent', {
            tenantCode: metadata.tenantCode,
            correlationToken: metadata.correlationToken,
            callDirection: callDetails?.callDirection,
        }));
    };

    private _websocketCallback = (message: { data: string }) => {
        if (this._stopRequested) {
            return;
        }
        if (typeof message.data === 'string') {
            const type = this._resolveResponseEnum(message.data);
            switch (type) {
                case RttResponseEnum.OK:
                    this._handleOkResponse();
                    break;
                case RttResponseEnum.READY:
                    this._handleReadyResponse();
                    break;
                case RttResponseEnum.CONTINUE:
                    this._handleContinueResponse();
                    break;
                case RttResponseEnum.TRANSCRIPT:
                    this._handleTranscriptResponse(message.data);
                    break;
                case RttResponseEnum.BOOKMARK:
                    this._handleBookmarkResponse(message.data);
                    break;
                case RttResponseEnum.MANUAL_BOOKMARK:
                    this._handleManualBookmarkResponse(message.data);
                    break;
                case RttResponseEnum.ERROR:
                    this._handleErrorResponse(message.data);
                    break;
                default:
                    logger.warn(
                        'Rtt: unknown server message (first bytes)',
                        LogTitle,
                        this._rttMeta('ws_message_unknown', { sample: (message.data || '').slice(0, 200) })
                    );
            }
        }
    };

    private _handleOkResponse = () => {
        if (this._stopRequested || !this._canHandshakeAndSend()) {
            return;
        }
        if (!this._isWsOpen()) {
            return;
        }
        if (this._connected && this._metadataSent && !this._processing) {
            this._websocket?.send(RttCommandsEnum.START_PROCESSING);
            logger.info('Rtt: got OK, sent start_processing', LogTitle, this._rttMeta('rtt_ok_start_processing'));
        }
    };

    /**
     * After a mid-call WS drop, the new RTASP session has no mic/phone binding.
     * Tear down and rebuild encoders so the next READY cycle pushes fresh `stream_mic` /
     * `stream_phone` on this socket (CallRecordId in metadata is what binds the call).
     */
    private _restartAudioPumpsForNewWsSession = (reason: string) => {
        logger.info(
            'Rtt: full audio restart for new WS session (re-bind stream_mic / stream_phone)',
            LogTitle,
            this._rttMeta('ws_ready_audio_restart', { reason, bufferLen: this._buffer.length })
        );
        this._teardownMicEncoder();
        this._teardownPhoneEncoder();
        this._ensureMicAttached(reason);
        this._schedulePhoneRebuild(reason);
    };

    private _handleReadyResponse = () => {
        if (this._stopRequested || !this._canHandshakeAndSend()) {
            return;
        }
        this._processing = true;
        this._sendInFlight = 0;
        if (!this._isFlushingAfterStop()) {
            if (this._needsFullAudioRestartOnReady) {
                this._needsFullAudioRestartOnReady = false;
                this._restartAudioPumpsForNewWsSession('ws_reconnect_ready');
            } else {
                this._ensureAudioPumpsRunning('ws_ready');
            }
        }
        logger.info('Rtt: got READY, streaming _sendBuffer', LogTitle, this._rttMeta('rtt_ready', {
            bufferLen: this._buffer.length,
            hasMicRecorder: this._micMediaRecorder != null,
            hasPhoneRecorder: this._phoneMediaRecorder != null,
        }));
        // Kick the credit loop: send first stream_* chunk on this socket so the server binds tracks.
        this._sendBuffer();
        this._tryFinishPostStopFlush();
    };

    private _handleContinueResponse = () => {
        if (this._stopRequested) {
            return;
        }
        this._sendInFlight = Math.max(0, this._sendInFlight - 1);
        this._sendBuffer();
        this._tryFinishPostStopFlush();
    };

    private _handleTranscriptResponse = (data: string) => {
        const transcriptJson = data.substring(RttResponseEnum.TRANSCRIPT.length).trim();
        const transcriptionSegment = JSON.parse(transcriptJson);

        if (!transcriptionSegment.transcript) { return; }

        var TRANSCRIPTMC = "rocketphone__TRANSCRIPTMC__c";

        if (transcriptionSegment.isFinal) {
            SaleforceService.publishObject({ callRecordId: this._callRecordId, segment: transcriptionSegment }, TRANSCRIPTMC);
            store.dispatch(addCallTranscript({ callRecordId: this._callRecordId, transcript: transcriptionSegment }));
            store.dispatch(setInterimTranscription({ callRecordId: this._callRecordId, transcript: null, source: transcriptionSegment.source }));
        } else {
            store.dispatch(setInterimTranscription({ callRecordId: this._callRecordId, transcript: transcriptionSegment, source: transcriptionSegment.source }));
        }
    };

    renderBookmarkType = (bookmarkType: string) => {
        let result = '';

        switch (bookmarkType) {
            case 'Case':
                result = 'Case';
                break;
            case 'Opportunity':
                result = 'Opportunity';
                break;
            case 'ScheduleCallback':
                result = 'ScheduleCallback';
                break;
            default:
                result = '';
        }

        return result;
    };

    private _handleBookmarkResponse = (data: string) => {
        const bookmarkJson = data.substring(RttResponseEnum.BOOKMARK.length).trim();
        const bookmarkCaptureMessage = JSON.parse(bookmarkJson);
        logger.info(
            'Rtt: bookmark from server (RTT)',
            LogTitle,
            this._rttMeta('rtt_bookmark', { bookmarkKeys: bookmarkCaptureMessage ? Object.keys(bookmarkCaptureMessage).slice(0, 12) : [] })
        );
        if (bookmarkCaptureMessage) {
            RealTimeClient.handleBookmarkResponse(bookmarkCaptureMessage);
            // store.dispatch(addBookmark(bookmarkCaptureMessage));
            // // screen Pop to specifid action with bookmark id as ref
            // const { callData: { id: callRecordId, actions: rawActions, bookmarks } } = bookmarkCaptureMessage;
            // console.log(LogTitle, "bookmarkCaptureMessage actions", rawActions);

            // if(bookmarks?.length > 0){
            //     const lastBookmark = bookmarks[bookmarks.length - 1];
            //     let bookmarkObject = {bookmarkType: 'All', bookmarkData: lastBookmark};
            //     var BOOKMARKMC = "BOOKMARKMC__c";
            //     SaleforceService.publishObject(bookmarkObject, BOOKMARKMC);
            //     console.log(LogTitle, "bookmarkObject streamProcess", bookmarkObject);
            // }

            // if (rawActions?.length > 0) {
            //     const lastElement = rawActions[rawActions.length - 1];
            //     let isExist = this._camturedBookmark.find(b => b?.id === lastElement.id);
            //     if (isExist) {
            //         return;
            //     }

            //     if ((lastElement?.actionType === 'Case' || lastElement?.actionType === 'Opportunity' || lastElement?.actionType === 'ScheduleCallback') && !lastElement?.remoteId) {
            //         const referenceId = `${lastElement.id}|${callRecordId}`;
            //         const callingContact = store.getState()?.callingContact?.data;
            //         let contactType = callingContact?.contactType;
            //         let contactRemoteId = callingContact?.remoteId;
                    
            //         let defaultValue = {}
            //         let objectName = null;

            //         if (lastElement?.actionType === 'ScheduleCallback') {
            //             if (contactType === 'Contact' || contactType === 'Lead') {
            //                 objectName = 'Task';
            //                 defaultValue = { ReferenceId__c: referenceId, WhoId: contactRemoteId }
            //             } else if (contactType === 'Account') {
            //                 objectName = 'Task';
            //                 defaultValue = { ReferenceId__c: referenceId, WhatId: contactRemoteId }
            //             } 
            //         } else {
            //             if (contactType === 'Contact' || contactType === 'Account') {
            //                 objectName = lastElement?.actionType === 'Case' ? 'Case': 'Opportunity';
            //                 if (contactType === 'Contact') {
            //                     defaultValue = { ReferenceId__c: referenceId, ContactId: contactRemoteId }
            //                 } else {
            //                     defaultValue = { ReferenceId__c: referenceId, AccountId: contactRemoteId }
            //                 }
            //             }
            //         }

            //         var SAMPLEMC = "SAMPLEMC__c";

            //         if (objectName && contactRemoteId) {
            //           this._camturedBookmark.push(lastElement);
            //           const bookmark = bookmarks.find((bm: any) => bm.id === lastElement?.actionTriggerId);
            //           console.log('bookmark for publishing:', bookmark);
            //           const snackbarData = { bookmarkType: this.renderBookmarkType(lastElement?.actionType) };
            //           store.dispatch(showSnackbar({ snackbarType: SnackbarType.Bookmark, snackbarData }));

            //           SaleforceService.openObjectCreationModal(objectName, defaultValue);
            //           SaleforceService.publishObject(bookmark, SAMPLEMC);
            //         }
            //     }
            // }
        }
    };

    private _handleManualBookmarkResponse = (data: string) => {
        // const manualbookmarkJson = data.substring(RttResponseEnum.MANUAL_BOOKMARK.length).trim();
        // const { type, bookmarkMarker, bookmarkRequest, callRecordId } = JSON.parse(manualbookmarkJson);
        // switch (type) {
        //     case RttManualBookmarkEventType.Commit:
        //         // store.dispatch(AddBookmarkFeature.Actions.trackManualBookmarkCommit(callRecordId, bookmarkMarker, bookmarkRequest));
        //         break;
        //     case RttManualBookmarkEventType.CommitSuccess:
        //         // store.dispatch(AddBookmarkFeature.Actions.trackManualBookmarkCommitSuccess(callRecordId, bookmarkMarker, bookmarkRequest));
        //         break;
        //     case RttManualBookmarkEventType.CommitFailure:
        //         // store.dispatch(AddBookmarkFeature.Actions.trackManualBookmarkCommitFailure(callRecordId, bookmarkMarker, bookmarkRequest));
        //         break;
        // }
    };

    private _handleErrorResponse = (data: string) => {
        logger.error('Rtt: server ERROR response (RTAP)', LogTitle, this._rttMeta('rtt_server_error', { payload: (data || '').slice(0, 500) }));
    };

    private _waitAndRetry = debounce(() => {
        this._sendBuffer();
    }, 1000);

    private _resolveResponseEnum = (data: string): RttResponseEnum => {
        if (data.startsWith(RttResponseEnum.OK)) {
            return RttResponseEnum.OK;
        }

        if (data.startsWith(RttResponseEnum.READY)) {
            return RttResponseEnum.READY;
        }

        if (data.startsWith(RttResponseEnum.CONTINUE)) {
            return RttResponseEnum.CONTINUE;
        }

        if (data.startsWith(RttResponseEnum.ERROR)) {
            return RttResponseEnum.ERROR;
        }

        if (data.startsWith(RttResponseEnum.TRANSCRIPT)) {
            return RttResponseEnum.TRANSCRIPT;
        }

        if (data.startsWith(RttResponseEnum.BOOKMARK)) {
            return RttResponseEnum.BOOKMARK;
        }

        if (data.startsWith(RttResponseEnum.MANUAL_BOOKMARK)) {
            return RttResponseEnum.MANUAL_BOOKMARK;
        }

        return RttResponseEnum.UNKNOWN;
    }
}

export default StreamProcessService;